import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { tint } from '@frontend/colors';
import { theme } from '@frontend/theme';
import { AvailabilityExceptionInfo, CalendarStripeLineInfo, WorkingHourInfo } from '../types';
import { zIndex } from '../utils';
import { useZoomSafeContext } from '../zoom/zoom-context';

const TIME_FORMAT = 'hh:mm A';
const MAX_DAY_MINUTES = 24 * 60;

const INDICATOR_TYPE = {
  NonWorkingHours: 'non-working-hours', // this is the full day stripe background
  WorkingHours: 'working-hours', // this is the working hours white background
  BreakHours: 'break-hours', // this is the break hours stripe background
  AvailabilityException: 'availability-exception', // this is the availability exception hours gray background
  ScheduleDefinitionException: 'schedule-definition-exception',
} as const;

type WorkingHoursIndicatorType = (typeof INDICATOR_TYPE)[keyof typeof INDICATOR_TYPE];

type IndicatorColorInfo = {
  background: string;
  stripe?: string;
};

const COLORS: Record<WorkingHoursIndicatorType, IndicatorColorInfo> = {
  [INDICATOR_TYPE.NonWorkingHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [INDICATOR_TYPE.WorkingHours]: {
    background: theme.colors.white,
  },
  [INDICATOR_TYPE.BreakHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [INDICATOR_TYPE.AvailabilityException]: {
    background: theme.colors.neutral5,
  },
  [INDICATOR_TYPE.ScheduleDefinitionException]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
};

const DEBUG_COLORS: Record<WorkingHoursIndicatorType, IndicatorColorInfo> = {
  [INDICATOR_TYPE.NonWorkingHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [INDICATOR_TYPE.WorkingHours]: {
    background: theme.colors.white,
  },
  [INDICATOR_TYPE.BreakHours]: {
    background: theme.colors.critical5,
    stripe: theme.colors.critical20,
  },
  [INDICATOR_TYPE.AvailabilityException]: {
    background: theme.colors.success10,
  },
  [INDICATOR_TYPE.ScheduleDefinitionException]: {
    background: theme.colors.critical5,
    stripe: theme.colors.critical20,
  },
};

const getStartMinutes = (startTime?: string): number =>
  startTime ? dayjs(startTime, TIME_FORMAT).diff(dayjs().startOf('day'), 'minute') : 0;

interface WorkingHoursIndicatorProps {
  workingHours?: WorkingHourInfo[];
  availabilityExceptions?: AvailabilityExceptionInfo[];
  isProvider?: boolean;
  hasAnyWorkingHoursSet?: boolean;
  isAllDayOff?: boolean;
  debug?: boolean;
  showStripes?: boolean;
}

interface ScheduleDefinitionHoursIndicatorProps {
  workingHours?: CalendarStripeLineInfo[];
  exceptions?: CalendarStripeLineInfo[];
  debug?: boolean;
  showStripes?: boolean;
}

export const WorkingHoursIndicator = ({
  workingHours,
  availabilityExceptions,
  isProvider = false,
  hasAnyWorkingHoursSet = true,
  isAllDayOff = false,
  debug = false,
  showStripes = true,
}: WorkingHoursIndicatorProps) => {
  const { pixelsPerMinute } = useZoomSafeContext();

  const defaultProps = { debug, isProvider, pxPerMinutes: pixelsPerMinute, showStripes };

  return (
    <div css={{ width: '100%', height: '100%', position: 'absolute', zIndex: zIndex.workingHoursContainer, left: 0 }}>
      {/* when no working hours are set for location, the whole day need to be shown as non-working hours */}
      {((!isProvider && !hasAnyWorkingHoursSet) || isAllDayOff) && (
        <IndicatorItem
          startTime='00:00 AM'
          duration={MAX_DAY_MINUTES}
          type={INDICATOR_TYPE.NonWorkingHours}
          {...defaultProps}
        />
      )}
      {hasAnyWorkingHoursSet && !isAllDayOff && (
        <>
          <IndicatorItem
            startTime='00:00 AM'
            duration={MAX_DAY_MINUTES}
            type={INDICATOR_TYPE.NonWorkingHours}
            {...defaultProps}
          />
          {workingHours?.map((workingHour) => (
            <div key={`working-hours-${workingHour.startTime}-${workingHour.duration}`}>
              <IndicatorItem
                startTime={workingHour.startTime}
                duration={workingHour.duration}
                type={INDICATOR_TYPE.WorkingHours}
                {...defaultProps}
              />
              {workingHour.breaks?.map(({ startTime, duration }) => (
                <IndicatorItem
                  key={`break-hours-${workingHour.startTime}-${startTime}-${duration}`}
                  startTime={startTime}
                  duration={duration}
                  type={INDICATOR_TYPE.BreakHours}
                  {...defaultProps}
                />
              ))}
            </div>
          ))}
        </>
      )}
      {availabilityExceptions?.map(({ startTime, duration }) => (
        <IndicatorItem
          key={`availability-exception-${startTime}-${duration}`}
          startTime={startTime}
          duration={duration}
          type={INDICATOR_TYPE.AvailabilityException}
          {...defaultProps}
        />
      ))}
    </div>
  );
};

export const ScheduleDefinitionHoursIndicator = ({
  workingHours,
  exceptions,
  debug = false,
  showStripes = true,
}: ScheduleDefinitionHoursIndicatorProps) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const defaultProps = { debug, isProvider: false, pxPerMinutes: pixelsPerMinute, showStripes };
  const hasWorkingHours = !!workingHours?.length;

  return (
    <div css={{ width: '100%', height: '100%', position: 'absolute', zIndex: zIndex.workingHoursContainer, left: 0 }}>
      {!hasWorkingHours && (
        <IndicatorItem
          startTime='00:00 AM'
          duration={MAX_DAY_MINUTES}
          type={INDICATOR_TYPE.NonWorkingHours}
          {...defaultProps}
        />
      )}
      {hasWorkingHours && (
        <>
          <IndicatorItem
            startTime='00:00 AM'
            duration={MAX_DAY_MINUTES}
            type={INDICATOR_TYPE.NonWorkingHours}
            {...defaultProps}
          />
          {workingHours?.map(({ startTime, duration }) => (
            <IndicatorItem
              key={`schedule-definition-working-hours-${startTime}-${duration}`}
              startTime={startTime}
              duration={duration}
              type={INDICATOR_TYPE.WorkingHours}
              {...defaultProps}
            />
          ))}
        </>
      )}
      {exceptions?.map(({ startTime, duration }) => (
        <IndicatorItem
          key={`schedule-definition-exception-${startTime}-${duration}`}
          startTime={startTime}
          duration={duration}
          type={INDICATOR_TYPE.ScheduleDefinitionException}
          {...defaultProps}
        />
      ))}
    </div>
  );
};

interface IndicatorItemProps extends CalendarStripeLineInfo {
  type: WorkingHoursIndicatorType;
  isProvider: boolean;
  debug: boolean;
  pxPerMinutes: number;
  showStripes: boolean;
}

const IndicatorItem = ({
  startTime,
  duration = 0,
  type,
  debug,
  isProvider,
  pxPerMinutes,
  showStripes,
}: IndicatorItemProps) => {
  if (!startTime || duration <= 0) {
    return null;
  }

  const startMinutes = getStartMinutes(startTime);
  const { background, stripe } = debug ? DEBUG_COLORS[type] : COLORS[type];
  return (
    <div
      data-debug={debug ? `${type}-${startTime}-${duration}` : undefined}
      css={[
        hoursRectangleStyle(startMinutes, duration, pxPerMinutes),
        stripeBackground(background, showStripes ? stripe : undefined, isProvider),
      ]}
    />
  );
};

const hoursRectangleStyle = (startMinutes = 0, duration = 0, pxPerMinutes: number) => {
  // safeguard for negative start minutes
  if (startMinutes + duration > MAX_DAY_MINUTES) {
    duration = MAX_DAY_MINUTES - startMinutes;
  }

  return css({
    position: 'absolute',
    top: startMinutes * pxPerMinutes,
    height: duration * pxPerMinutes,
    width: '100%',
  });
};

const stripeBackground = (bgColor: string, stripeColor?: string, isProvider?: boolean) => {
  if (!stripeColor || stripeColor === bgColor) {
    return css({
      background: bgColor,
    });
  }

  return css({
    background: `repeating-linear-gradient(135deg, ${bgColor} 0 0.6rem, ${tint(
      isProvider ? 0.25 : 0.5,
      stripeColor
    )} 0.6rem 0.9rem)`,
    backgroundPosition: '0 0',
    backgroundRepeat: 'repeat',
    backgroundAttachment: 'fixed',
  });
};
